"use client";

import { WorkplaceRequestTypesResponse } from "@gettactic/api";
import { useWorkplaceRequestsAllowed } from "@gettactic/hooks";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api/api";

export type SubmittableWorkplaceRequestTypesFilters = {
  /**
   * Restricts the list to the types usable at that resource. Wins over
   * {@link SubmittableWorkplaceRequestTypesFilters.officeId} — the API takes one
   * location filter or the other.
   */
  resourceId?: string;
  /** Restricts the list to the types usable in that office. */
  officeId?: string;
};

/**
 * Request types the current user can submit (the `/v1/tickets/me/types` endpoint).
 * Used by the "Submit a request" wizard.
 *
 * Pass the location whenever it is already known — filing against a map resource, a map
 * pin or a reservation — so the picker cannot offer a type that place does not have.
 * Callers that need the list as a lookup table (resolving the type of an existing
 * request) should ask for it unfiltered instead.
 *
 * The endpoint answers with every type in one page — it takes neither `limit` nor
 * `cursor` — so there is nothing to page through.
 *
 * Nothing is fetched unless the org has workplace requests enabled: the endpoint
 * 403s otherwise, and the wizard mounts (and queries) while still closed.
 */
export function useSubmittableWorkplaceRequestTypes({
  resourceId,
  officeId
}: SubmittableWorkplaceRequestTypesFilters = {}) {
  const isWorkplaceRequestsAllowed =
    useWorkplaceRequestsAllowed("workplace_requests");
  const { data, isFetching } = useQuery<WorkplaceRequestTypesResponse>({
    queryKey: [
      "workplaceRequestTypes",
      { submittable: true, resourceId, officeId }
    ],
    queryFn: async () => {
      const res =
        await api.client.workplaceRequestTypes.getWorkplaceRequestTypes({
          isAdmin: false,
          resource_id: resourceId,
          office_id: officeId
        });
      return res.result as WorkplaceRequestTypesResponse;
    },
    enabled: isWorkplaceRequestsAllowed
  });

  const allElements = data?.elements ?? [];
  return { allElements, isFetching };
}
